import { useState } from 'react'
import styled, { css } from 'styled-components/macro'
import Panel, { PanelTitle, PanelSpacer } from 'components/layout/Panel'
import { H4, H6, Body2, Body1 } from 'components/layout/text'
import Spacer from 'components/misc/Spacer'
import { Modal } from 'roadie-ui'
import useScrolled from 'util/useScrolled'
import { AnchorButton } from 'components/layout/buttons'
import media from 'styles/mixins/media'
import Icon from 'components/misc/Icon'
import { BasicButton } from 'components/layout/buttons'
import LightboxClose from 'rui/widgets/LightboxClose'
import { LocalizedText } from 'localization'

const PromotionPanel = styled(Panel)`
  background: ${(props) => props.theme.colors.background};
`

const LightBoxWrapper = styled.div`
  max-width: 46.4rem;
`

const PromotionWrapper = styled.div`
  background: ${(props) => props.theme.colors.white};
  display: flex;
  width: 100%;
  cursor: pointer;
  &:not(:first-child) {
    border-top: 1px solid ${(props) => props.theme.colors.greyLight};
    ${media.mobile`
      margin-top: 1.6rem;
    `}
  }
  ${media.mobile`
    flex-direction: column;
    &:not(:last-child) {
      border-bottom: 1px solid ${(props) => props.theme.colors.greyLight};
    }
  `}
`

const PromotionImage = styled.div`
  width: 100%;
  max-width: 16rem;
  height: 12rem;
  background-image: url(${(props) => props.src});
  background-size: cover;

  ${media.mobile`
    max-width: 100%;
    padding-top: 56%;
    height: 0;
  `}
`

const PromotionTitle = styled(H6)`
  ${(props) => (props.mobile ? media('desktop', 'tablet', 'ultrawide') : media.mobile)`
    display: none;
  `}
  padding: ${(props) => (props.mobile ? '1.6rem 2.4rem' : 0)};
`

const LightboxImage = styled.img`
  width: 100%;
  vertical-align: middle;
`

const PromotionDetails = styled.div`
  padding: 1.6rem 2.4rem;
  text-align: left;
`

const Empty = styled.div`
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  ${media.mobile`
    flex-grow: 1;
  `}

  ${media('desktop', 'tablet', 'ultrawide')`
    height: 60rem;
  `}
`

const ScrollableArea = styled.div`
  max-height: 60vh;
  overflow-y: scroll;
  overflow-x: hidden;
`

const ContentContainer = styled.div`
  padding: 4rem 4rem 0 4rem;
  background: ${(props) => props.theme.colors.white};
  width: 100%;
`

const ButtonsContainer = styled.div`
  padding: 0 4rem 4rem 4rem;
  background: ${(props) => props.theme.colors.white};
  width: 100%;

  ${(props) =>
    props.incidatescroll &&
    css`
      box-shadow: 0px -1px 6px rgba(0, 0, 0, 0.5);
      z-index: 1;
    `}
`

const ButtonWrap = styled.div`
  display: flex;
  justify-content: center;
`

const Lightbox = (props) => {
  const [scrolled, scrollRef] = useScrolled()
  return (
    <LightBoxWrapper>
      <ScrollableArea>
        {props.lightbox}
        <ContentContainer>
          <H4 align="center">{props.title}</H4>
          <Spacer height="1.6rem" />
          {props.children}
          <span ref={scrollRef} />
        </ContentContainer>
      </ScrollableArea>
      <ButtonsContainer incidatescroll={!scrolled}>
        {props.actionButton && (
          <>
            <Spacer height="1.6rem" />
            {props.actionButton}
          </>
        )}
        {props.onRequestClose && (
          <>
            <Spacer height="1.6rem" />
            <ButtonWrap>
              <BasicButton onClick={props.onRequestClose}>
                <LocalizedText translationKey="dweb_misc_promotions_dismiss" />
              </BasicButton>
            </ButtonWrap>
          </>
        )}
      </ButtonsContainer>
      <LightboxClose onClick={props.onRequestClose} />
    </LightBoxWrapper>
  )
}

const Promotion = (props) => {
  const [showingModal, setShowingModal] = useState(false)
  return (
    <PromotionWrapper onClick={() => setShowingModal(true)}>
      <PromotionTitle align="left" mobile>
        {props.title}
      </PromotionTitle>
      <PromotionImage src={props.thumbnail} />
      <PromotionDetails>
        <PromotionTitle>{props.title}</PromotionTitle>
        <Spacer height=".8rem" />
        <Body2 pre>{props.body}</Body2>
      </PromotionDetails>
      {showingModal && (
        <Modal.Raw>
          <Lightbox
            actionButton={
              <AnchorButton href={props.site} target="_blank" mode="primary">
                {props.buttonText}
              </AnchorButton>
            }
            title={props.modalTitle || props.title}
            lightbox={<LightboxImage src={props.image} />}
            onRequestClose={() => setShowingModal(false)}
          >
            <Body1 pre>{props.modalBody || props.body}</Body1>
            <Spacer height="1.6rem" />
          </Lightbox>
        </Modal.Raw>
      )}
    </PromotionWrapper>
  )
}

const Section = (props) => (
  <>
    <PanelTitle>{props.category}</PanelTitle>
    <PromotionPanel noPadding>
      {props.items.map((item) => {
        return (
          <Promotion
            key={item.id}
            image={item.image}
            thumbnail={item.thumbnail}
            title={item.title}
            body={item.summary}
            modalBody={item.description}
            buttonText={item.buttonText || 'Go To Site'}
            site={item.url}
          />
        )
      })}
    </PromotionPanel>
    <PanelSpacer />
  </>
)

const Promotions = (props) => {
  const { items, categories, catItemMap } = props.data
  if (categories.length) {
    return categories.map((category) => {
      const sectionItems = catItemMap[category].map((id) => items.find((item) => item.id === id))
      return <Section key={category} category={category} items={sectionItems} />
    })
  } else {
    return (
      <Empty>
        <Icon symbol="news-paper" palette="primary" shade="dark" />
        <Spacer height="2.4rem" />
        <p>
          <LocalizedText translationKey="dweb_misc_promotions_no_news_good_news" />
        </p>
        <p>
          <LocalizedText translationKey="dweb_misc_promotions_please_check_back_later" />
        </p>
      </Empty>
    )
  }
}

export default Promotions
